/**
 * Features API Service
 *
 * Handles feature flag management for multi-tenant control.
 * Supports platform-level (superadmin) and organization-level (admin) feature toggles.
 */

import { API_BASE, getAuthHeaders, handleResponse } from './apiClient';

// =============================================================================
// Feature Definitions
// =============================================================================

/**
 * List all available feature definitions
 */
export const listFeatureDefinitions = async (user) => {
  const headers = await getAuthHeaders(user);
  const response = await fetch(`${API_BASE}/features/definitions`, {
    headers,
  });
  return handleResponse(response);
};

// =============================================================================
// Organization Feature Endpoints
// =============================================================================

/**
 * Get all features for an organization
 */
export const getOrgFeatures = async (user, orgId) => {
  const headers = await getAuthHeaders(user);
  const response = await fetch(`${API_BASE}/features/org/${orgId}`, {
    headers,
  });
  return handleResponse(response);
};

/**
 * Get status of a specific feature for an organization
 */
export const getFeatureStatus = async (user, orgId, featureKey) => {
  const headers = await getAuthHeaders(user);
  const response = await fetch(`${API_BASE}/features/org/${orgId}/${featureKey}`, {
    headers,
  });
  return handleResponse(response);
};

/**
 * Quick check if a feature is enabled (lightweight endpoint)
 */
export const checkFeatureEnabled = async (user, orgId, featureKey) => {
  const headers = await getAuthHeaders(user);
  const response = await fetch(`${API_BASE}/features/check/${orgId}/${featureKey}`, {
    headers,
  });
  return handleResponse(response);
};

/**
 * Toggle feature at organization level (admin)
 */
export const toggleOrgFeature = async (user, orgId, featureKey, enabled) => {
  const headers = await getAuthHeaders(user);
  const response = await fetch(`${API_BASE}/features/org/${orgId}/${featureKey}/org`, {
    method: 'PUT',
    headers,
    body: JSON.stringify({ enabled }),
  });
  return handleResponse(response);
};

/**
 * Update feature settings for an organization
 */
export const updateFeatureSettings = async (user, orgId, featureKey, settings) => {
  const headers = await getAuthHeaders(user);
  const response = await fetch(`${API_BASE}/features/org/${orgId}/${featureKey}/settings`, {
    method: 'PUT',
    headers,
    body: JSON.stringify({ settings }),
  });
  return handleResponse(response);
};

// =============================================================================
// Superadmin Platform-Level Endpoints
// =============================================================================

/**
 * Toggle feature at platform level (superadmin)
 */
export const togglePlatformFeature = async (user, orgId, featureKey, enabled) => {
  const headers = await getAuthHeaders(user);
  const response = await fetch(`${API_BASE}/features/org/${orgId}/${featureKey}/platform`, {
    method: 'PUT',
    headers,
    body: JSON.stringify({ enabled }),
  });
  return handleResponse(response);
};

/**
 * Get feature status across all organizations (superadmin)
 */
export const getAllOrgsFeatureStatus = async (user, featureKey) => {
  const headers = await getAuthHeaders(user);
  const response = await fetch(`${API_BASE}/features/admin/all?feature_key=${encodeURIComponent(featureKey)}`, {
    headers,
  });
  return handleResponse(response);
};

/**
 * Bulk toggle feature for multiple organizations (superadmin)
 */
export const bulkToggleFeature = async (user, featureKey, orgIds, enabled) => {
  const headers = await getAuthHeaders(user);
  const response = await fetch(`${API_BASE}/features/admin/bulk?feature_key=${encodeURIComponent(featureKey)}`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ org_ids: orgIds, enabled }),
  });
  return handleResponse(response);
};

// =============================================================================
// Feature Keys Enum (for type safety in JS)
// =============================================================================

export const FeatureKeys = {
  SANDBOX_LAB: 'sandbox_lab',
};

export default {
  listFeatureDefinitions,
  getOrgFeatures,
  getFeatureStatus,
  checkFeatureEnabled,
  toggleOrgFeature,
  updateFeatureSettings,
  togglePlatformFeature,
  getAllOrgsFeatureStatus,
  bulkToggleFeature,
  FeatureKeys,
};
